/* tslint:disable:only-arrow-functions */
/* tslint:disable:no-nested-template-literals */

import {
  BoughtThisSeasonProviderConfigArgs,
  OrderTypeType,
  SearchSortType,
} from 'models-recommendation';

import {
  RecommendedDataType,
  RecommendedProductDataType,
} from '../product-data';
import { getDataForBoughtThisSeason } from '../tenants/common/bought-this-season/bought-this-season-helper';
import {
  BrandOrgType,
  getBrandOrganisation,
} from '../tenants/common/bought-this-season/brand-org';

const defaultBoughtThisSeasonProviderArgs: BoughtThisSeasonProviderConfigArgs =
  {
    numberOfDaysFrom: 180,
    numberOfDaysTo: 0,
    productsAmount: 55,
    loadPrices: true,
    filterMissingImages: false,
  } as BoughtThisSeasonProviderConfigArgs;

const BOUGHT_THIS_SEASON_CONSTANTS = {
  accountId: {
    provider: 'GlobalStoreProviderService',
    property: {
      path: 'accounts.activeAccount.accountId',
    },
  },
  brandOrganisation: getBrandOrganisation(),
  brandId: {
    provider: 'self',
    property: 'brandOrganisation',
    transform: function (brandOrg: BrandOrgType): string {
      return brandOrg.brandId;
    }.toString(),
  },
  fromDate: {
    provider: 'self',
    property: 'numberOfDaysFrom',
    transform: function (val: number) {
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - val);
      return fromDate.toISOString().substring(0, 10);
    }.toString(),
  },
  toDate: {
    provider: 'self',
    property: 'numberOfDaysTo',
    transform: function (val: number) {
      const toDate = new Date();
      toDate.setDate(toDate.getDate() - val);
      return toDate.toISOString().substring(0, 10);
    }.toString(),
  },
  orderType: null,
  type: null,
  dateRangeField: null,
  aggregationType: null,
  groupByPropertyName: 'masterProduct.productId',
  boughtThisSeasonProviderConfig: {
    values: [
      {
        provider: 'self',
        property: 'productsAmount',
      },
      {
        provider: 'self',
        property: 'fromDate',
      },
      {
        provider: 'self',
        property: 'toDate',
      },
      {
        provider: 'self',
        property: 'filterMissingImages',
      },
      {
        provider: 'self',
        property: 'loadPrices',
      },
      {
        provider: 'self',
        property: 'isDelivered',
      },
      {
        provider: 'self',
        property: 'isGlobalNoos',
      },
      {
        provider: 'self',
        property: 'searchSortType',
      },
      {
        provider: 'self',
        property: 'type',
      },
      {
        provider: 'self',
        property: 'orderType',
      },
      {
        provider: 'self',
        property: 'dateRangeField',
      },
      {
        provider: 'self',
        property: 'aggregationType',
      },
      {
        provider: 'self',
        property: 'selectedCategory',
      },
      {
        provider: 'self',
        property: 'selectedSortByMostBought',
      },
      {
        provider: 'self',
        property: 'selectedHeaderPresetResolved',
      },
    ],
    transform: function ([
      productsAmount,
      fromDate,
      toDate,
      filterMissingImages,
      loadPrices,
      isDelivered,
      isGlobalNoos,
      searchSortType,
      type,
      orderType,
      dateRangeField,
      aggregationType,
      category,
      sortDirection,
      selectedHeaderPreset,
    ]: [
      number,
      string,
      string,
      boolean,
      boolean,
      boolean,
      boolean,
      SearchSortType,
      OrderTypeType,
      number,
      number,
      number,
      string[],
      number[],
      { [key: string]: string | number },
    ]): BoughtThisSeasonProviderConfigArgs {
      const providerConfigArgs: Partial<BoughtThisSeasonProviderConfigArgs> = {
        productsAmount,
        fromDate,
        toDate,
        filterMissingImages,
        loadPrices,
        isDelivered,
        isGlobalNoos,
        searchSortType,
        type,
        orderType,
        dateRangeField,
        aggregationType,
        category: category?.[0],
        sortDirection: sortDirection?.[0],
        ...selectedHeaderPreset,
      };

      return providerConfigArgs as BoughtThisSeasonProviderConfigArgs;
    }.toString(),
  },
  boughtThisSeasonData: getDataForBoughtThisSeason(
    { provider: 'self', property: 'panelName' },
    { provider: 'self', property: 'brandId' },
    {
      provider: 'self',
      property: 'boughtThisSeasonProviderConfig',
    },
    { provider: 'self', property: 'groupByPropertyName' },
  ),
  productData: {
    provider: 'self',
    property: 'boughtThisSeasonData',
    transform: function (
      data: RecommendedDataType,
    ): RecommendedProductDataType[] {
      if (
        window.neoclesDynamicFramework.recommendationsPanels.methods.isResolveValueError(
          data,
        )
      ) {
        throw new Error(
          'Expected products but encountered an error: ' +
            data.__resolveValueError__,
        );
      }
      return data.products;
    }.toString(),
  },
  servingConfig: {
    provider: 'self',
    property: 'boughtThisSeasonData',
    transform: function (data: RecommendedDataType) {
      return data.servingConfig;
    }.toString(),
  },
};

export const BOUGHT_THIS_SEASON_PROVIDERS_PROPERTIES = {
  isDelivered: null,
  isGlobalNoos: null,
  searchSortType: null,
  numberOfDaysFrom: defaultBoughtThisSeasonProviderArgs.numberOfDaysFrom,
  numberOfDaysTo: defaultBoughtThisSeasonProviderArgs.numberOfDaysTo,
  productsAmount: defaultBoughtThisSeasonProviderArgs.productsAmount,
  filterMissingImages: defaultBoughtThisSeasonProviderArgs.filterMissingImages,
  loadPrices: defaultBoughtThisSeasonProviderArgs.loadPrices,
  selectedCategory: null,
  selectedSortByMostBought: null,
  hasHeaderFilters: false,
  categoryParameter: 'product',
  selectedHeaderPreset: {
    provider: 'id:preselect-header-select',
    property: 'toggleFilter',
    startWith: ['0'],
  },
  selectedHeaderPresetResolved: {
    provider: 'id:root',
    property: 'selectedHeaderPreset',
    // tslint:disable-next-line:only-arrow-functions
    transform: function (selectedHeaderPreset: string) {
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 180);
      const buyAgainFilterPresets = [
        // goto
        {
          aggregationType: 0,
          dateRangeField: 0,
        },
        // buy again
        {
          aggregationType: 1,
          dateRangeField: 1,
          fromDate: fromDate.toISOString().substring(0, 10),
          type: null,
          orderType: null,
        },
      ];
      return buyAgainFilterPresets[Number(selectedHeaderPreset)];
    }.toString(),
  },
  ...BOUGHT_THIS_SEASON_CONSTANTS,
  seeMoreLinkResolved: {
    values: [
      {
        provider: 'id:root',
        property: 'selectedCategory',
      },
      {
        provider: 'id:root',
        property: 'selectedSortByMostBought',
      },
      {
        provider: 'id:root',
        property: 'seeMoreLink',
      },
      {
        provider: 'id:root',
        property: 'fromDate',
      },
      {
        provider: 'id:root',
        property: 'toDate',
      },
      {
        provider: 'id:root',
        property: 'categoryParameter',
      },
      {
        provider: 'id:root',
        property: 'type',
      },
    ],
    transform: function ([
      selectedCategory,
      selectedSortByMostBought,
      seeMoreLink,
      fromDate,
      toDate,
      categoryParameter,
      type,
    ]: [string, string, string, string, string, string, string]): string {
      if (seeMoreLink) {
        return seeMoreLink;
      }
      const url = `/browse/search?${
        selectedCategory
          ? `f=${categoryParameter}:` + selectedCategory + '&'
          : ''
      }${
        type ? `preset=` + type + '&' : ''
      }sortField=BoughtThisSeason&sortDirection=${
        selectedSortByMostBought?.[0] === '0' ? 'Asc' : 'Desc'
      }&start=0&num=25&orderHistoryFromDate=${fromDate}&orderHistoryToDate=${toDate}`;
      return url;
    }.toString(),
  },
};
